import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from './AuthProvider'
import { useSessionValidation } from '@/hooks/useSessionValidation'

interface ProtectedRouteProps {
  children: ReactNode
  requiredRole?: string
}

/**
 * Protected Route Component
 * Feature: 008-better-auth-integration
 * 
 * Protects routes requiring authentication and specific roles.
 * Uses Better Auth session for authentication state.
 */
export default function ProtectedRoute({ children, requiredRole = 'admin' }: ProtectedRouteProps) {
  const { user, loading, isAdmin, hasRole } = useAuth()
  const location = useLocation()
  const { t } = useTranslation('common')

  // Session validation with periodic checks
  const { isValidating } = useSessionValidation({
    enabled: !!user && !loading,
    interval: 60000, // Check every minute
  })

  // Show loading spinner while checking auth or validating session
  if (loading || isValidating) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
        <div className="text-lg text-gray-600">
          {user ? t('api.loading') : 'Checking authentication...'}
        </div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!user) {
    // Check if this is a session expiry redirect
    const state = location.state as { sessionExpired?: boolean; message?: string } | null
    
    return (
      <Navigate
        to="/admin/login"
        replace
        state={{
          from: location.pathname,
          sessionExpired: state?.sessionExpired,
          message: state?.message || (state?.sessionExpired ? t('api.errors.session_expired') : undefined),
        }}
      />
    )
  }

  // Show unauthorized if authenticated but missing required role
  // For admin role, use isAdmin (which checks for 'admin' in comma-separated roles)
  // For other roles, use hasRole function
  const hasRequiredRole = requiredRole === 'admin' ? isAdmin : hasRole(requiredRole)
  if (!hasRequiredRole) {
    return <Navigate to="/admin/unauthorized" replace />
  }

  return <>{children}</>
}
